import type { Campaign, CreateCampaignInput, UpdateCampaignInput } from '@/domain/campaign.types'
import type {
	CampaignCommandPort,
	CampaignListFilter,
	CampaignListPageRequest,
	CampaignQueryPort,
} from '@/domain/campaign-ports'
import { campaignService } from '@/services/campaign.service'
import { mapCampaignHttpError } from './campaign-http-error'

// HTTP-backed CampaignQueryPort + CampaignCommandPort — call campaign.service.ts
// and map every failure through mapCampaignHttpError. The preserved in-memory
// campaign-crud-adapter.ts is untouched; this is a new, independent module.

export const campaignQueryHttpAdapter: CampaignQueryPort = {
	async listCampaigns(filter: CampaignListFilter, page?: CampaignListPageRequest): Promise<Campaign[]> {
		try {
			// `scenario` is a fixture-only test/story hook — the HTTP adapter never consults it.
			// The port's optional page request is forwarded as-is: the service owns query
			// serialization, and defaulting or dropping `page`/`limit` here would pin every
			// read to the first page and break load-more.
			return await campaignService.listCampaigns({
				status: filter.status,
				classification: filter.classification,
				page: page?.page,
				limit: page?.limit,
			})
		} catch (error) {
			throw mapCampaignHttpError(error)
		}
	},
	async getCampaign(campaignId: string): Promise<Campaign> {
		try {
			return await campaignService.getCampaign(campaignId)
		} catch (error) {
			throw mapCampaignHttpError(error)
		}
	},
}

export const campaignCommandHttpAdapter: CampaignCommandPort = {
	async createCampaign(input: CreateCampaignInput): Promise<Campaign> {
		try {
			return await campaignService.createCampaign(input)
		} catch (error) {
			throw mapCampaignHttpError(error)
		}
	},
	async updateCampaign(campaignId: string, input: UpdateCampaignInput): Promise<Campaign> {
		try {
			// campaignUpdateSchema.strict().refine(len > 0) rejects a zero-key body (422) —
			// an empty patch is a no-op read, never a wire PATCH.
			if (Object.keys(input).length === 0) return await campaignService.getCampaign(campaignId)
			return await campaignService.updateCampaign(campaignId, input)
		} catch (error) {
			throw mapCampaignHttpError(error)
		}
	},
	async deleteCampaign(campaignId: string): Promise<void> {
		try {
			await campaignService.deleteCampaign(campaignId)
		} catch (error) {
			throw mapCampaignHttpError(error)
		}
	},
}
